/* matcherSearchIndex.js
 *
 * Curated index that maps free-text search queries to one of three
 * destinations the homepage search bar can route to:
 *
 *   1. `linkTo` route       full-page tool (Find a Builder wizard, etc.)
 *   2. `buildType` href     opens our new BuildTypePopup directly
 *   3. matcher popup        MiniMatcher with a pre-set category
 *
 * The label/tag are what render in the dropdown. Keywords are the
 * real "search engine" - rich and synonym-aware so a tradesperson's
 * vernacular ("sparkie", "chippy", "QS", "NatHERS", "alfresco")
 * still maps to the right pro.
 *
 * IMPORTANT: do not strip casual synonyms or trade slang. People
 * search with their own words.
 */

export const MATCHER_INDEX = [
  // ── Build-type popups ─────────────────────────────────────
  // These are listed first so build-type intent always beats
  // tradie/professional intent. e.g. "renovation" opens the popup
  // (which itself surfaces both a renovation-builder matcher and
  // designer matchers), not the single matcher.
  {
    kind: 'build_type', buildType: '/building-a-new-home',
    label: 'Building a new home', tag: 'Build type',
    keywords: ['new home', 'new build', 'new homes', 'building a new home',
               'first home', 'home build', 'house build', 'building from scratch',
               'turn key', 'turnkey', 'volume builder', 'project home',
               'custom home', 'custom build', 'spec build'],
  },
  {
    kind: 'build_type', buildType: '/renovations',
    label: 'Renovating your home', tag: 'Build type',
    keywords: ['renovation', 'renovations', 'reno', 'renos', 'renovate',
               'home reno', 'house reno', 'extension', 'extensions',
               'second storey', 'second story', 'addition', 'home addition',
               'bathroom reno', 'kitchen reno', 'whole house renovation',
               'remodel', 'remodelling', 'remodeling'],
  },
  {
    kind: 'build_type', buildType: '/choosing-land',
    label: 'Choosing a block of land', tag: 'Build type',
    keywords: ['block of land', 'land block', 'vacant land', 'buying land',
               'land for sale', 'choosing land', 'choosing a block',
               'block assessment', 'site costs', 'soil test', 'soil class',
               'reactive soil', 'slope', 'sloping block', 'easement',
               'overlay', 'flood zone', 'bushfire zone'],
  },
  {
    kind: 'build_type', buildType: '/changing-builders',
    label: 'Changing builders mid-project', tag: 'Build type',
    keywords: ['change builder', 'changing builder', 'sack my builder',
               'fire builder', 'switch builder', 'builder bankrupt',
               'builder went bust', 'builder liquidation', 'builder dispute',
               'left in the lurch', 'half built', 'walked off site'],
  },
  {
    kind: 'build_type', buildType: '/property-development',
    label: 'Invest or develop property', tag: 'Build type',
    keywords: ['property development', 'develop', 'developer', 'subdivision',
               'subdivide', 'duplex', 'townhouse', 'townhouses', 'investment',
               'investment property', 'rental yield', 'depreciation strategy',
               'multi unit', 'unit development'],
  },
  {
    kind: 'build_type', buildType: '/verandahs-pergolas',
    label: 'Verandahs & pergolas', tag: 'Build type',
    keywords: ['verandah', 'verandahs', 'pergola', 'pergolas', 'alfresco',
               'patio', 'outdoor entertaining', 'outdoor room', 'awning'],
  },
  {
    kind: 'build_type', buildType: '/landscaping',
    label: 'Landscaping your build', tag: 'Build type',
    keywords: ['landscaping', 'landscape design', 'garden design',
               'hard landscaping', 'soft landscaping', 'retaining wall',
               'paving', 'turf', 'irrigation', 'lawn'],
  },
  {
    kind: 'build_type', buildType: '/sheds-garages',
    label: 'Sheds & garages', tag: 'Build type',
    keywords: ['shed', 'sheds', 'garage', 'garages', 'workshop',
               'carport', 'colorbond shed', 'farm shed', 'industrial shed'],
  },
  {
    kind: 'build_type', buildType: '/decking',
    label: 'Decking', tag: 'Build type',
    keywords: ['deck', 'decking', 'composite decking', 'timber decking',
               'merbau', 'modwood', 'trex', 'spotted gum deck'],
  },
  {
    kind: 'build_type', buildType: '/interior-design',
    label: 'Interior design', tag: 'Build type',
    keywords: ['interior design', 'interior styling', 'styling',
               'colour consult', 'colour palette', 'selections',
               'interior fitout'],
  },
  {
    kind: 'build_type', buildType: '/home-design',
    label: 'Home design', tag: 'Build type',
    keywords: ['home design', 'house design', 'house plans', 'floor plan',
               'floor plans', 'home plans', 'design ideas', 'architect plans',
               'concept design'],
  },
  {
    kind: 'build_type', buildType: '/swimming-pools',
    label: 'Swimming pools', tag: 'Build type',
    keywords: ['swimming pool build', 'pool installation', 'inground pool',
               'fibreglass pool', 'concrete pool', 'plunge pool', 'lap pool'],
  },

  // ── Find a Builder (full wizard) ──────────────────────────
  {
    category: 'professional:builder',
    label: 'Find a New Home Builder',
    tag: 'Wizard',
    keywords: ['builder', 'builders', 'find a builder', 'new home builder',
               'home builder', 'house builder', 'project builder',
               'volume builder', 'custom builder', 'kdr builder',
               'compare builders', 'builder match'],
    linkTo: '/workspace/tools/find-a-builder',
  },

  // ── Approvals & design professionals ──────────────────────
  {
    category: 'professional:architect',
    label: 'Find an Architect',
    tag: 'Professional',
    keywords: ['architect', 'architects', 'architecture', 'architectural',
               'concept design', 'design and document', 'da drawings',
               'bespoke design', 'custom design'],
  },
  {
    category: 'professional:building_designer',
    label: 'Find a Building Designer',
    tag: 'Professional',
    keywords: ['building designer', 'drafter', 'drafting', 'drafty',
               'concept plans', 'working drawings', 'da plans',
               'council drawings'],
  },
  {
    category: 'professional:interior_designer',
    label: 'Find an Interior Designer',
    tag: 'Professional',
    keywords: ['interior designer', 'colour consultant', 'colorconsultant',
               'styling consult', 'kitchen design', 'bathroom design',
               'selections consultant'],
  },
  {
    category: 'professional:town_planner',
    label: 'Find a Town Planner',
    tag: 'Professional',
    keywords: ['town planner', 'planner', 'town planning', 'da',
               'development application', 'council approval', 'planning permit',
               'planning report', 'heritage approval', 'overlay assessment'],
  },
  {
    category: 'professional:surveyor',
    label: 'Find a Surveyor',
    tag: 'Professional',
    keywords: ['surveyor', 'surveyors', 'land surveyor', 'site surveyor',
               'survey', 'contour survey', 'feature survey',
               'identification survey', 'id survey', 'site set-out', 'setout',
               'boundary', 'site plan', 'site survey', 'level survey'],
  },
  {
    category: 'professional:energy_assessor',
    label: 'Find an Energy Assessor',
    tag: 'Professional',
    keywords: ['energy assessor', 'energy rating', 'nathers', 'na thers',
               '6 star', 'six star', 'thermal performance', 'energy report',
               'basix', 'bers'],
  },

  // ── Engineering ────────────────────────────────────────────
  {
    category: 'professional:structural_engineer',
    label: 'Find a Structural Engineer',
    tag: 'Professional',
    keywords: ['structural engineer', 'engineer', 'engineering',
               'slab design', 'footing design', 'beam design',
               'retaining wall engineer', 'underpinning engineer'],
  },
  {
    category: 'professional:civil_engineer',
    label: 'Find a Civil Engineer',
    tag: 'Professional',
    keywords: ['civil engineer', 'civil engineering', 'stormwater',
               'drainage design', 'subdivision works', 'civils'],
  },
  {
    category: 'professional:quantity_surveyor',
    label: 'Find a Quantity Surveyor',
    tag: 'Professional',
    keywords: ['quantity surveyor', 'qs', 'tax depreciation',
               'depreciation schedule', 'cost estimator', 'cost plan',
               'cost report', 'building cost report', 'estimator'],
  },
  {
    category: 'professional:building_inspector',
    label: 'Find a Building Inspector',
    tag: 'Professional',
    keywords: ['building inspector', 'inspector', 'inspection',
               'pre purchase inspection', 'pci', 'practical completion',
               'handover inspection', 'frame inspection', 'slab inspection',
               'lockup inspection', 'pre-handover', 'post-handover', 'defect'],
  },

  // ── Finance & legal ────────────────────────────────────────
  {
    category: 'professional:finance_broker',
    label: 'Find a Finance Specialist',
    tag: 'Professional',
    keywords: ['finance', 'finance broker', 'finance specialist',
               'mortgage broker', 'loan', 'lending', 'construction loan',
               'pre-approval', 'preapproval', 'borrow', 'borrowing',
               'first home', 'fhog', 'fhb', 'first home buyer',
               'stamp duty', 'deposit', 'low doc'],
  },
  {
    category: 'professional:conveyancer',
    label: 'Find a Conveyancer',
    tag: 'Professional',
    keywords: ['conveyancer', 'conveyancing', 'land contract',
               'contract review', 'cooling off', 'easement',
               'encumbrance', 'settlement', 'section 32'],
  },
  {
    category: 'professional:property_lawyer',
    label: 'Find a Property Lawyer',
    tag: 'Professional',
    keywords: ['property lawyer', 'lawyer', 'solicitor', 'building lawyer',
               'construction lawyer', 'building dispute',
               'builder dispute', 'building tribunal'],
  },
  {
    category: 'professional:insurance_broker',
    label: 'Find an Insurance Specialist',
    tag: 'Professional',
    keywords: ['insurance', 'insurance broker', 'home insurance',
               'building insurance', 'construction insurance',
               'home warranty', 'public liability builder'],
  },

  // ── Specialist builders ────────────────────────────────────
  {
    category: 'professional:renovation_builder',
    label: 'Find a Renovation Builder',
    tag: 'Specialist Builder',
    keywords: ['renovation builder', 'reno builder', 'extension builder',
               'addition builder', 'reno specialist'],
  },
  {
    category: 'professional:pergola_builder',
    label: 'Find a Pergola Builder',
    tag: 'Specialist Builder',
    keywords: ['pergola builder', 'verandah builder', 'patio builder',
               'alfresco builder'],
  },
  {
    category: 'professional:deck_builder',
    label: 'Find a Deck Builder',
    tag: 'Specialist Builder',
    keywords: ['deck builder', 'decking builder', 'decking installer'],
  },
  {
    category: 'professional:shed_builder',
    label: 'Find a Shed Builder',
    tag: 'Specialist Builder',
    keywords: ['shed builder', 'garage builder', 'colorbond shed',
               'workshop builder'],
  },
  {
    category: 'professional:granny_flat_builder',
    label: 'Find a Granny Flat Builder',
    tag: 'Specialist Builder',
    keywords: ['granny flat', 'granny flat builder', 'secondary dwelling',
               'studio dwelling', 'backyard cabin', 'tiny home builder'],
  },

  // ── Partners ──────────────────────────────────────────────
  {
    category: 'partner:equable',
    label: 'Talk finance with Equable Home Loans',
    tag: 'Partner · Finance',
    keywords: ['equable', 'equable home loans'],
  },
  {
    category: 'partner:house_inspect',
    label: 'Book an independent inspection with House Inspect Australia',
    tag: 'Partner · Inspection',
    // SA-only surface. The `saOnly` flag is honoured by
    // searchMatchers({ isSA }) - HIA is hidden from non-SA hero
    // searches so users interstate aren't shown a partner they can't
    // access. Keywords cover the full HIA service catalogue that we
    // want to surface: general building inspections, pre-purchase,
    // stage inspections during a new build, PCI / handover /
    // post-handover, pool compliance (SA legal requirement) and
    // pest (via sister brand Pest Protect Australia). The vanilla
    // `professional:building_inspector` entry still exists for
    // non-SA users and users typing generic terms.
    saOnly: true,
    keywords: [
      // Brand and generic
      'house inspect', 'house inspect australia', 'hia inspection',
      'independent inspection',
      // Generic building inspection (was previously only served by
      // professional:building_inspector - now HIA is co-served for SA)
      'building inspection', 'building inspector', 'home inspection',
      // Pre-purchase (buying an established home)
      'pre purchase inspection', 'prepurchase inspection',
      'pre-purchase inspection', 'buyer inspection',
      // New build stage inspections
      'slab inspection', 'frame inspection', 'lockup inspection',
      'lock up inspection', 'stage inspection', 'new build inspection',
      // PCI / handover / defects
      'pci', 'practical completion inspection', 'handover inspection',
      'pre-handover inspection', 'post-handover inspection',
      'defect inspection', 'defect report', 'snag list', 'snags',
      // Pool compliance (SA legal requirement before selling a
      // property with a pool or spa)
      'pool compliance', 'pool compliance inspection',
      'pool certificate', 'pool safety certificate', 'pool fence certificate',
      // Pest (bundled with pre-purchase context)
      'pest inspection', 'pest and building inspection',
      'termite inspection', 'timber pest inspection',
    ],
  },

  // ── Tradies ────────────────────────────────────────────────
  { category: 'tradie:tiler',        label: 'Find a Tiler',        tag: 'Trade',
    keywords: ['tile', 'tiler', 'tilers', 'tiling', 'splashback', 'mosaic',
               'porcelain tile', 'ceramic tile', 'feature tile'] },
  { category: 'tradie:painter',      label: 'Find a Painter',      tag: 'Trade',
    keywords: ['paint', 'painter', 'painters', 'painting', 'house painter',
               'interior painter', 'exterior painter'] },
  { category: 'tradie:plumber',      label: 'Find a Plumber',      tag: 'Trade',
    keywords: ['plumber', 'plumbers', 'plumbing', 'leak', 'leaking',
               'blocked drain', 'tap', 'hot water', 'rough in plumbing',
               'gas plumber'] },
  { category: 'tradie:electrician',  label: 'Find an Electrician', tag: 'Trade',
    keywords: ['electric', 'electrical', 'electrician', 'electricians',
               'sparky', 'sparkie', 'sparkies', 'switchboard',
               'powerpoint', 'downlights', 'lighting', 'rough in electrical'] },
  { category: 'tradie:concreter',    label: 'Find a Concreter',    tag: 'Trade',
    keywords: ['concreter', 'concretor', 'concrete', 'concreting', 'driveway',
               'slab', 'slab pour', 'footings', 'exposed aggregate',
               'polished concrete'] },
  { category: 'tradie:carpenter',    label: 'Find a Carpenter',    tag: 'Trade',
    keywords: ['carpenter', 'carpenters', 'chippy', 'chippies', 'carpentry',
               'framing', 'frame carpenter', 'second fix carpenter'] },
  { category: 'tradie:roofer',       label: 'Find a Roofer',       tag: 'Trade',
    keywords: ['roofer', 'roofers', 'roofing', 'gutter', 'guttering',
               'downpipe', 'colorbond roof', 'tile roof', 'roof restoration',
               'roof leak'] },
  { category: 'tradie:plasterer',    label: 'Find a Plasterer',    tag: 'Trade',
    keywords: ['plasterer', 'plasterers', 'plaster', 'plastering',
               'gyprock', 'gypsum', 'cornice', 'render', 'rendering',
               'set plaster'] },
  { category: 'tradie:flooring',     label: 'Find a Flooring Installer', tag: 'Trade',
    keywords: ['flooring', 'floor installer', 'carpet', 'vinyl', 'vinyl plank',
               'laminate', 'hybrid floor', 'timber floor', 'engineered timber',
               'spc flooring', 'lvt'] },
  { category: 'tradie:cabinetmaker', label: 'Find a Cabinetmaker', tag: 'Trade',
    keywords: ['cabinetmaker', 'cabinet maker', 'cabinetry', 'kitchen install',
               'kitchen builder', 'wardrobe', 'walk in robe', 'wir',
               'joinery', 'joiner', 'vanity'] },
  { category: 'tradie:landscaper',   label: 'Find a Landscaper',   tag: 'Trade',
    keywords: ['landscaper', 'landscapers', 'lawn', 'turf', 'irrigation',
               'retaining', 'paving', 'mulch', 'softscape'] },
  { category: 'tradie:fencer',       label: 'Find a Fencing Contractor', tag: 'Trade',
    keywords: ['fence', 'fences', 'fencer', 'fencing', 'pool fence',
               'colorbond fence', 'timber fence', 'glass fence'] },
  { category: 'tradie:pool_builder', label: 'Find a Pool Builder', tag: 'Trade',
    keywords: ['pool builder', 'pool builders', 'fibreglass pool',
               'concrete pool', 'plunge pool', 'lap pool', 'pool install'] },
  { category: 'tradie:waterproofer', label: 'Find a Waterproofer', tag: 'Trade',
    keywords: ['waterproof', 'waterproofer', 'waterproofing', 'membrane',
               'shower waterproofing', 'balcony waterproofing'] },
  { category: 'tradie:glazier',      label: 'Find a Glazier',      tag: 'Trade',
    keywords: ['glazier', 'glaziers', 'glass', 'window install',
               'shower screen', 'glass splashback', 'mirror', 'glazing'] },
  { category: 'tradie:hvac',         label: 'Find an HVAC Specialist', tag: 'Trade',
    keywords: ['hvac', 'air con', 'aircon', 'air conditioning',
               'split system', 'ducted', 'ducted aircon', 'ducted heating',
               'heating cooling', 'evaporative', 'reverse cycle'] },
  { category: 'tradie:solar',        label: 'Find a Solar Installer', tag: 'Trade',
    keywords: ['solar', 'pv', 'pv system', 'solar panel', 'solar panels',
               'battery', 'home battery', 'solar hot water', 'solar inverter'] },
  { category: 'tradie:stonemason',   label: 'Find a Stonemason',   tag: 'Trade',
    keywords: ['stone', 'stonemason', 'benchtop', 'caesarstone',
               'engineered stone', 'natural stone', 'marble', 'granite'] },
  { category: 'tradie:handyman',     label: 'Find a Handyman',     tag: 'Trade',
    keywords: ['handyman', 'handyperson', 'odd job', 'minor repair',
               'small job'] },
];

/* Build a normalised plural/singular alternate for each keyword on
 * demand so callers don't have to list both. This keeps the index
 * concise without sacrificing recall. */
function alternates(kw) {
  const out = new Set([kw]);
  // crude plural → singular fold
  if (kw.endsWith('ies')) out.add(kw.slice(0, -3) + 'y');
  else if (kw.endsWith('es') && kw.length > 4) out.add(kw.slice(0, -2));
  else if (kw.endsWith('s') && !kw.endsWith('ss') && kw.length > 3) out.add(kw.slice(0, -1));
  // singular → plural
  if (!kw.endsWith('s')) out.add(kw + 's');
  return [...out];
}

/** Lightweight Levenshtein (≤1 edit) so a single typo still finds
 *  a hit (e.g. "surveyer" → "surveyor", "pergolaz" → "pergolas",
 *  "concretor" → "concreter"). */
function fuzzyEqual(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    let diffs = 0;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        diffs += 1;
        if (diffs > 1) return false;
      }
    }
    return true;
  }
  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) { i += 1; j += 1; }
    else { edits += 1; j += 1; if (edits > 1) return false; }
  }
  return true;
}

export function searchMatchers(rawQuery, { limit = 4, isSA = false } = {}) {
  const q = (rawQuery || '').toLowerCase().trim();
  if (q.length < 2) return [];
  const hits = [];
  for (const m of MATCHER_INDEX) {
    // Honour saOnly gating - hide SA-only entries from non-SA
    // searchers so users interstate aren't shown a partner they
    // cannot access.
    if (m.saOnly && !isSA) continue;
    let score = 0;
    for (const baseKw of m.keywords) {
      for (const kw of alternates(baseKw)) {
        if (kw === q)             { score = Math.max(score, 110); break; }
        if (kw.startsWith(q))     { score = Math.max(score, 90); continue; }
        if (q.startsWith(kw))     { score = Math.max(score, 88); continue; }
        if (kw.includes(q))       { score = Math.max(score, 70); continue; }
        if (q.includes(kw))       { score = Math.max(score, 60); continue; }
        // fuzzy single-typo fallback, only for tokens long enough to
        // make the comparison meaningful (4+ chars)
        if (q.length >= 4 && kw.length >= 4 && fuzzyEqual(q, kw)) {
          score = Math.max(score, 50);
        }
      }
    }
    // Slight boost for build-type popups so a generic phrase like
    // "renovation" routes to the popup over a single matcher.
    if (m.kind === 'build_type' && score > 0) score += 8;
    // Boost for a partner:* result when the user is in SA - we want
    // House Inspect Australia to outrank the generic building
    // inspector matcher for SA searchers so they see the vetted
    // partner first.
    if (m.category?.startsWith('partner:') && isSA && score > 0) {
      score += 15;
    }
    if (score > 0) hits.push({ ...m, score });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, limit);
}
